<template>
  <v-container fluid class="d-flex flex-column pa-0 h-100">
    <v-row
      no-gutters
      v-if="success"
      class="h-100 d-flex justify-center align-center"
    >
      <v-col cols="auto">
        <SuccessBillMessage />
      </v-col>
    </v-row>

    <v-row
      no-gutters
      class="h-100 d-flex justify-center align-center"
      v-else-if="showErrorAlert"
    >
      <v-col cols="auto">
        <AlertMessage>
          <template #icon>
            <v-icon color="red" size="40"> highlight_off </v-icon>
          </template>
          <template #title>
            <span
              class="text-subtitle-1 text-md-h6 font-weight-bold text-center mb-1"
            >
              Ocurrió un error al cargar la factura
            </span>
          </template>
          <template #description>
            <div
              class="text-subtitle-2 text-md-body-1 font-weight-regular grey--text text--darken-1 text-center"
            >
              <p class="mb-0">
                Para resolverlo, comunicate con el Área de Prestadores por
              </p>
              <p class="mb-0">
                mail a
                <span class="font-weight-bold">expertos@vaitty.com</span>
              </p>
            </div>
          </template>
          <template #actions>
            <div
              class="d-flex w-100 flex-column align-center"
              style="row-gap: 1rem"
            >
              <v-btn
                color="blue darken-2"
                class="white--text"
                large
                @click="closeErrorAlert"
              >
                Volver a seleccionar servicios
              </v-btn>
              <v-btn
                color="blue darken-2"
                large
                text
                @click="$router.push({ name: 'bill-list' })"
              >
                Volver al inicio
              </v-btn>
            </div>
          </template>
        </AlertMessage>
      </v-col>
    </v-row>

    <v-row
      no-gutters
      v-else
      class="d-flex flex-column h-100"
      style="gap: 1.5rem"
    >
      <v-col class="flex-grow-0">
        <h6 class="text-h6 text-md-h5 font-weight-bold">Facturación</h6>
      </v-col>
      <div class="left-col d-flex flex-column" style="gap: 1.5rem">
        <v-alert
          dense
          color="#FB8C00"
          class="ma-0"
          icon="report_problem"
          type="warning"
          width="100%"
        >
          No pudimos verificar los datos de la factura, por favor cargalos
          manualmente a continuación.
        </v-alert>
        <v-alert
          v-if="hasDuplicateError && errorMessage"
          dense
          type="error"
          icon="report"
          width="100%"
          class="ma-0"
        >
          {{ errorMessage }}
        </v-alert>
        <v-row no-gutters class="d-flex flex-column" style="gap: 1.5rem">
          <v-col v-if="formData.invoice">
            <UploadedInvoice :invoice="formData.invoice" />
          </v-col>

          <v-col>
            <h3 class="text-subtitle-1 text-md-h6 font-weight-bold mb-4">
              Validar datos
            </h3>
            <v-form
              ref="invoiceForm"
              lazy-validation
              v-model="valid"
              @submit.prevent="handleSubmit"
            >
              <v-container class="pa-0" fluid>
                <v-row class="form-row-gutter-16">
                  <v-col cols="12">
                    <v-menu
                      ref="menu"
                      v-model="dateMenu"
                      :close-on-content-click="false"
                      :return-value.sync="formData.date"
                      transition="scale-transition"
                      offset-y
                      min-width="auto"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          :value="formattedDate"
                          outlined
                          label="Fecha de la factura"
                          prepend-inner-icon="mdi-calendar"
                          readonly
                          v-bind="attrs"
                          v-on="on"
                          hide-details="auto"
                          :rules="[(v) => !!v || 'Debe seleccionar una fecha']"
                        ></v-text-field>
                      </template>
                      <v-date-picker
                        v-model="formData.date"
                        no-title
                        scrollable
                      >
                        <v-spacer></v-spacer>
                        <v-btn text color="primary" @click="dateMenu = false">
                          Cancelar
                        </v-btn>
                        <v-btn
                          text
                          color="primary"
                          @click="$refs.menu.save(formData.date)"
                        >
                          OK
                        </v-btn>
                      </v-date-picker>
                    </v-menu>
                  </v-col>
                  <v-col cols="12">
                    <v-row
                      :dense="$vuetify.breakpoint.mdAndUp"
                      class="d-flex align-baseline form-row-gutter-16"
                    >
                      <v-col :cols="$vuetify.breakpoint.smAndDown ? 12 : 3">
                        <v-text-field
                          label="Pto. de venta"
                          outlined
                          v-model.trim="part1"
                          maxlength="5"
                          @blur="formatPart($event, 'part1', 5)"
                          :rules="[
                            rules.required,
                            rules.integer,
                            rules.notAllZeros('Pto. Vta.'),
                          ]"
                          hide-details="auto"
                        ></v-text-field>
                      </v-col>
                      <v-col
                        v-if="$vuetify.breakpoint.mdAndUp"
                        class="flex-grow-0"
                        ><span
                          class="font-weight-medium text-subtitle-1 grey--text text--darken-1"
                        >
                          &mdash;</span
                        ></v-col
                      >
                      <v-col cols="auto" class="flex-grow-1">
                        <v-text-field
                          label="Nro. de comprobante"
                          outlined
                          v-model.trim="part2"
                          maxlength="8"
                          @blur="formatPart($event, 'part2', 8)"
                          :rules="[
                            rules.required,
                            rules.integer,
                            rules.notAllZeros('Nro. de comprobante'),
                          ]"
                          hide-details="auto"
                        ></v-text-field>
                      </v-col>
                    </v-row>
                  </v-col>
                  <v-col cols="12">
                    <v-text-field
                      label="CUIT Factura"
                      outlined
                      v-model.trim="cuitDisplay"
                      :rules="[
                        rules.required,
                        rules.cuitNumeric,
                        rules.cuitFormat,
                        rules.cuitNotZero,
                      ]"
                      maxLength="13"
                      @blur="formatCuitOn"
                      hide-details="auto"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12">
                    <v-text-field
                      label="Monto total"
                      outlined
                      prefix="$"
                      v-model.trim="formData.amount"
                      :rules="[
                        rules.required,
                        rules.amountNumeric,
                        rules.maxIntDigits({
                          fieldName: 'monto total',
                          digits: 8,
                        }),
                        rules.amountDecimals,
                        rules.amountNotZero,
                      ]"
                      hide-details="auto"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12">
                    <v-radio-group
                      v-model="formData.cbu"
                      :rules="[(v) => !!v || 'Campo requerido']"
                      required
                      column
                      class="ma-0"
                      hide-details="auto"
                    >
                      <template v-slot:label>
                        <div class="mb-2">
                          ¿En qué cuenta querés recibir el pago?
                        </div>
                      </template>
                      <div class="d-flex flex-column" style="gap: 1rem">
                        <v-radio
                          v-for="account of technicianAccounts"
                          :key="account.id"
                          :value="account.id"
                          color="blue darken-2"
                        >
                          <template v-slot:label>
                            <v-card
                              class="d-flex flex-column ml-2"
                              elevation="0"
                            >
                              <span
                                class="text-subtitle-2 text-md-subtitle-1 font-weight-bold"
                                >Nombre y Apellido:
                                <span class="font-weight-regular"
                                  >{{ account.nombre_prestador }}
                                  {{ account.apellido_prestador }}</span
                                ></span
                              >
                              <span
                                class="text-subtitle-2 text-md-subtitle-1 font-weight-bold"
                                >CUIT:
                                <span class="font-weight-regular">{{
                                  account.cuit
                                }}</span></span
                              >
                              <span
                                class="text-subtitle-2 text-md-subtitle-1 font-weight-bold"
                                >Banco:
                                <span class="font-weight-regular">{{
                                  account.banco
                                }}</span></span
                              >
                              <span
                                class="text-subtitle-2 text-md-subtitle-1 font-weight-bold"
                                >CBU:
                                <span class="font-weight-regular">{{
                                  account.cbu
                                }}</span></span
                              >
                              <v-alert
                                v-if="
                                  cuitWithoutDashes &&
                                  account.cuit === cuitWithoutDashes
                                "
                                type="success"
                                dense
                                text
                                class="mt-2"
                              >
                                Mismo CUIT que la factura cargada
                              </v-alert>
                            </v-card>
                          </template>
                        </v-radio>
                      </div>
                    </v-radio-group>
                  </v-col>
                  <v-col cols="12" class="mt-4">
                    <v-btn
                      type="submit"
                      block
                      depressed
                      large
                      color="blue darken-2 white--text"
                      :loading="isSending"
                    >
                      Enviar
                    </v-btn>
                  </v-col>
                </v-row>
              </v-container>
            </v-form>
          </v-col>
        </v-row>
      </div>
    </v-row>
  </v-container>
</template>

<script>
import { mapActions, mapMutations, mapState } from "vuex";
import SuccessBillMessage from "../components/SuccessBillMessage.vue";
import AlertMessage from "../components/AlertMessage.vue";
import UploadedInvoice from "../components/Form/UploadedInvoice.vue";

const DUPLICATE_INVOICE = "invoice_exist";
const CREATE_INVOICE_ERROR = "create_invoice_exception";

export default {
  name: "AlternativeFormView",
  components: {
    SuccessBillMessage,
    AlertMessage,
    UploadedInvoice,
  },
  data() {
    return {
      dateMenu: false,
      valid: false,
      success: false,
      part1: "",
      part2: "",
      hasDuplicateError: false,
      errorMessage: "",
      isSending: false,
      showErrorAlert: false,
      cuitDisplay: "",
      rules: {
        required: (v) => !!v || "Este campo es obligatorio",
        integer: (v) =>
          /^\d+$/.test(String(v)) || "Solo se permiten números enteros",
        maxIntDigits:
          ({ fieldName = "campo", digits = 8 } = {}) =>
          (v) => {
            const intPart = String(v || "").split(".")[0];
            return (
              intPart.length <= digits ||
              `El ${fieldName} no puede tener más de ${digits} dígitos`
            );
          },
        notAllZeros: (fieldname) => (v) =>
          !/^0+$/.test(String(v || "")) ||
          `El ${fieldname} no puede ser solo ceros`,
        cuitNumeric: (v) =>
          !/[^\d-]/.test(String(v || "")) ||
          "El CUIT solo puede contener números y guiones",
        cuitFormat: (v) =>
          /^\d{2}-\d{8}-\d{1}$/.test(String(v)) ||
          "El formato del CUIT es incorrecto (ej: 20-12345678-9)",
        cuitNotZero: (v) => {
          const digits = String(v || "").replace(/\D/g, "");
          return (
            (digits.length > 0 && !/^0+$/.test(digits)) ||
            "Ingresá un CUIT válido"
          );
        },
        amountNumeric: (v) =>
          /^\d+(\.\d*)?$/.test(String(v || "")) ||
          "El monto solo puede contener números y un punto decimal",
        amountDecimals: (v) => {
          const value = String(v || "");
          if (value.endsWith(".")) {
            return "Falta completar los decimales después del punto";
          }
          return (
            /^\d+(\.\d{1,2})?$/.test(value) ||
            "El monto admite hasta 2 decimales"
          );
        },
        amountNotZero: (v) => Number(v) > 0 || "El monto debe ser mayor a $0",
      },
    };
  },
  computed: {
    ...mapState("invoice", ["formData", "technicianAccounts"]),

    formattedInvoiceNumber() {
      if (!this.part1 && !this.part2) return null;
      return `${this.part1}-${this.part2}`;
    },

    cuitWithoutDashes() {
      return this.formData.cuit?.replace(/-/g, "");
    },
    formattedDate() {
      if (!this.formData?.date) return "";
      const [year, month, day] = this.formData.date.split("-");
      if (!year || !month || !day) return this.formData.date;
      return `${day}/${month}/${year}`;
    },
  },
  created() {
    this.getTechnicianAccounts();
  },
  mounted() {
    this.$store.commit("set_app_title", "Facturación");
    this.cuitDisplay = this.formatCuit(this.formData.cuit || "");
  },
  methods: {
    ...mapActions("invoice", [
      "sendInvoice",
      "handleFormDataChanges",
      "getTechnicianAccounts",
    ]),
    ...mapMutations("invoice", ["setState"]),

    parsePayload(formData = {}) {
      const { invoice, cbu, transactions, date, cuit, amount } = formData;
      const transactionIds = transactions.map((transaction) => transaction.id);

      const form = new FormData();
      form.append("archivo_factura", invoice);
      form.append("transacciones", transactionIds);
      form.append("cbu_field", cbu);
      form.append("fecha_factura", date);
      form.append("nro_factura", this.formattedInvoiceNumber);
      form.append("cuit_factura", cuit);
      form.append("monto_factura", amount);
      return form;
    },
    openPDFInNewTab() {
      if (this.formData?.invoice) {
        const blobUrl = URL.createObjectURL(this.formData.invoice);
        window.open(blobUrl, "_blank");
      }
    },
    async handleSubmit() {
      const isValid = this.$refs.invoiceForm.validate();
      if (!isValid) return;

      this.isSending = true;
      const newInvoice = this.parsePayload(this.formData);
      this.sendInvoice(newInvoice)
        .then(() => {
          this.$refs.invoiceForm.reset();
          this.success = true;
          this.hasDuplicateError = false;
          this.errorMessage = "";
        })
        .catch((error) => {
          if (error.isAxiosError) {
            const errorCode = error.response?.data?.error_code;
            if (errorCode === DUPLICATE_INVOICE) {
              this.hasDuplicateError = true;
              this.errorMessage =
                "La factura que intentas subir ya está registrada en el sistema. Revisá los datos antes de continuar.";
            } else if (errorCode === CREATE_INVOICE_ERROR) {
              this.showErrorAlert = true;
            }
          } else {
            console.log(error);
          }
        })
        .finally(() => {
          this.isSending = false;
        });
    },

    formatCuit(value) {
      const digits = String(value).replace(/\D/g, "").substring(0, 11);

      if (digits.length <= 2) return digits;
      if (digits.length <= 10) {
        return `${digits.substring(0, 2)}-${digits.substring(2)}`;
      }
      return `${digits.substring(0, 2)}-${digits.substring(
        2,
        10
      )}-${digits.substring(10, 11)}`;
    },

    formatCuitDisplay(raw) {
      const value = String(raw ?? "");
      const digits = value.replace(/\D/g, "").substring(0, 11);
      let formatted = this.formatCuit(digits);

      if (value.endsWith("-")) {
        if (digits.length === 2) {
          formatted = `${digits}-`;
        } else if (digits.length === 10) {
          formatted = `${digits.substring(0, 2)}-${digits.substring(2, 10)}-`;
        }
      }

      return formatted;
    },

    formatCuitOn() {
      const raw = String(this.cuitDisplay || "").trim();
      if (!/[^\d-]/.test(raw)) {
        this.cuitDisplay = this.formatCuitDisplay(raw);
      }
    },

    formatPart(event, partKey, length) {
      let value = event.target.value.replace(/[^0-9]/g, "");

      if (value.length === 0) {
        this[partKey] = "";
      } else if (value.length < length) {
        this[partKey] = value.padStart(length, "0");
      } else {
        this[partKey] = value.substring(0, length);
      }

      this.showErrors = true;
    },

    async closeErrorAlert() {
      this.showErrorAlert = false;
      const navigationResult = await this.$router.push({ name: "bill-form" });

      if (navigationResult) {
        // no hacer nada
      } else {
        this.setState();
      }
    },
  },
  watch: {
    formData: {
      handler(newValue) {
        this.handleFormDataChanges(newValue);
      },
      deep: true,
    },
    success(newValue) {
      if (newValue) {
        this.setState();
        localStorage.removeItem("pending-transactions");
      }
    },
    cuitDisplay(val) {
      const raw = String(val ?? "");
      this.formData.cuit = raw.replace(/\D/g, "").substring(0, 11);

      const hasInvalidChars = /[^\d-]/.test(raw);
      if (!hasInvalidChars) {
        const formatted = this.formatCuitDisplay(raw);
        if (formatted !== raw) {
          this.$nextTick(() => {
            this.cuitDisplay = formatted;
          });
        }
      }
    },
    "formData.cuit"(val) {
      if (val) {
        const preselect = this.technicianAccounts?.find(
          (account) => account.cuit === val
        );
        if (preselect) {
          this.formData.cbu = preselect.id;
        }
      }
    },
    formattedInvoiceNumber(val) {
      if (val) {
        this.formData.invoiceNumber = val;
      } else {
        this.formData.invoiceNumber = null;
      }
    },
  },
};
</script>

<style scoped>
.form-row-gutter-16 {
  margin: -8px;
}
.form-row-gutter-16 > .col,
.form-row-gutter-16 > [class*="col-"] {
  padding: 8px;
}

.short-text {
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
@media (max-width: 959px) {
  .left-col {
    width: 100%;
  }
}

@media (min-width: 960px) {
  .left-col {
    width: 65%;
  }
}
</style>
